import { Assistant, PinDrop } from "@mui/icons-material";
import {
  Badge,
  Box,
  IconButton,
  SvgIcon,
  SvgIconProps,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocationAvailability } from "../../../features/Booking-Form/typings/booking-location";
import { hasActiveBookingFilters } from "../../../features/Booking-Form/functions/booking-filter-state.functions";
import {
  formatLocationFilterMatchAria,
  formatLocationFilterMatchTooltip
} from "../../../features/Booking-Form/functions/booking-filter-match-tooltip.functions";
import { ConnectedBookingData } from "../../../features/Booking-Form/typings/connected-bookingData";
import { LocationAvailableIconPerBookingType } from "./LocationAvailableIconPerBookingType.partial";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { useFeatures } from "../../FacilityManager/Hooks/useFeatures";

type P = {
  location: LocationAvailability;
  isAutomatedSeatingAvailable: boolean | undefined;
  spotsAvailable: boolean | undefined;
  uniqConnectedBookingData: ConnectedBookingData[];
  locationIcons: JSX.Element;
  handleGoogleLocationClick(address: string): void;
  handleAutomatedSeatingClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
};

export function LocationCardMediaChildren({
  location,
  isAutomatedSeatingAvailable,
  spotsAvailable,
  locationIcons,
  handleGoogleLocationClick,
  handleAutomatedSeatingClick
}: Readonly<P>) {
  const { t } = useTranslation();
  const inputs = useSelector((state: RootState) => state.booking.inputs);
  const { useFindGoogleMapRoute } = useFeatures();

  const {
    name,
    address1,
    address2,
    postalCode,
    city,
    numberOfBookableObjects,
    numberOfFilterMatchedObjects,
    numberOfFilterFullMatches,
    numberOfFilterPartialMatches
  } = location;
  const showMatchedCount =
    hasActiveBookingFilters(inputs.bookingFilters) && numberOfFilterMatchedObjects != null;
  const matchCounts = {
    total: numberOfBookableObjects,
    full: numberOfFilterFullMatches,
    partial: numberOfFilterPartialMatches,
    matched: numberOfFilterMatchedObjects,
    bookingType: inputs.mode
  };

  const isMobileWorking = name === "Mobile Working";

  const handleGoogleLocClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    const addressFind = `${address1} ${address2} ${postalCode} ${city}`;
    handleGoogleLocationClick(addressFind);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 0.5,
        position: "relative" // Important for layering
      }}
      data-testid="location-chilren-container"
    >
      <Box
        sx={theme => ({
          display: "flex",
          justifyContent: "space-between",
          bgcolor: theme.palette.background.paper,
          // Position it under the child Box
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          opacity: 0.9,
          ...theme.applyStyles("light", {
            opacity: 0.95
          })
        })}
      />

      {!isMobileWorking && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            zIndex: 1,
            pr: 1
          }}
        >
          <Box>
            {/* google map link */}
            {useFindGoogleMapRoute && (
              <Tooltip
                title={<Typography>{t("_googleMapLocDesc")}</Typography>}
                aria-label={"_googleMapLocDesc"}
              >
                <IconButton
                  sx={{ opacity: 1 }}
                  data-testid="google-map-loc-btn"
                  onClick={handleGoogleLocClick}
                >
                  <PinDrop />
                </IconButton>
              </Tooltip>
            )}

            {/* select automatic seating */}
            {isAutomatedSeatingAvailable && (
              <Tooltip
                title={<Typography>{t("_freeWorkplaceChosen")}</Typography>}
                aria-label={"_freeWorkplaceChosen"}
              >
                <IconButton
                  sx={{ opacity: 1 }}
                  data-testid="automated-seating-loc-btn"
                  onClick={handleAutomatedSeatingClick}
                >
                  <Assistant />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ alignSelf: "center", pr: 0.5 }}>
            {spotsAvailable ? (
              <Box
                data-testid="loc-available-icon-box"
                sx={{ display: "flex", alignItems: "center" }}
              >
                {showMatchedCount ? (
                  <Tooltip
                    title={
                      <Typography>{formatLocationFilterMatchTooltip(matchCounts, t)}</Typography>
                    }
                  >
                    <Box
                      data-testid="loc-available-matched-count"
                      aria-label={formatLocationFilterMatchAria(matchCounts, t)}
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Badge
                        badgeContent={numberOfFilterMatchedObjects}
                        color="primary"
                        data-testid="loc-filter-match-badge"
                        sx={{ mr: 0.25 }}
                      >
                        <LocationStyledIconComp iconSize={{ width: "28px", height: "28px" }}>
                          <LocationAvailableIconPerBookingType inputs={inputs} />
                        </LocationStyledIconComp>
                      </Badge>
                      <Typography variant="h6" component="span">
                        {numberOfBookableObjects}
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : (
                  <>
                    <LocationStyledIconComp iconSize={{ width: "28px", height: "28px" }}>
                      <LocationAvailableIconPerBookingType inputs={inputs} />
                    </LocationStyledIconComp>
                    <Typography variant="h6" data-testid="loc-available-total-count">
                      {numberOfBookableObjects}
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              locationIcons
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

interface LocationStyledIconCompProps extends SvgIconProps {
  iconSize: { width: string; height: string };
}

export const LocationStyledIconComp: React.FC<LocationStyledIconCompProps> = ({
  iconSize,
  children,
  sx,
  ...rest
}) => {
  const theme = useTheme();

  return (
    <SvgIcon
      viewBox={`0 0 ${iconSize.width.slice(0, -2)} ${iconSize.height.slice(0, -2)}`}
      sx={{
        width: iconSize.width,
        height: iconSize.height,
        marginRight: "8px",
        "& path": {
          fill: theme.palette.text.primary
        },
        ...sx
      }}
      {...rest}
    >
      {children}
    </SvgIcon>
  );
};
